import { APP_INITIALIZER, LOCALE_ID, NgModule, provideAppInitializer } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { StoreModule } from '@ngrx/store';
import { ROOT_REDUCERS } from './store/reducers';
import { EffectsModule } from '@ngrx/effects';
import { ApplicationEffects } from './store/effects/application.effects';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { HttpService } from '@shared/services/http/http.service';
import { CxMaterialConfigProviders } from '@bbraun/cortex';
import { ServiceWorkerModule } from '@angular/service-worker';
import {
  BrowserCacheLocation,
  InteractionType,
  IPublicClientApplication,
  ProtocolMode,
  PublicClientApplication,
} from '@azure/msal-browser';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptorConfiguration,
  MsalModule,
  MsalRedirectComponent,
  MsalService,
} from '@azure/msal-angular';
import { environment } from '@environments/environment';
import { OfflineInterceptor } from '@shared/interceptors/app-offline-interceptor';
import { TranslocoRootModule } from './transloco-root.module';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { getAppLocale, registerAppLocales } from '@app/app.locales';
import { MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { DEFAULT_DIALOG_OPTIONS } from '@shared/models/dialog.model';
import { SharedModule } from '@shared/shared.module';
import { initSnackbar } from '@shared/services/snackbar/snackbar.service';

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.msalConfig.auth.clientId,
      redirectUri: environment.msalConfig.auth.redirectUri,
      authority: environment.b2cPolicies.authorities.signUpSignIn.authority,
      authorityMetadata: JSON.stringify({
        authorization_endpoint: environment.b2cPolicies.authorityMetadata.authorization_endpoint,
        token_endpoint: environment.b2cPolicies.authorityMetadata.token_endpoint,
        issuer: environment.b2cPolicies.authorityMetadata.issuer,
        end_session_endpoint: environment.b2cPolicies.authorityMetadata.end_session_endpoint,
        jwks_uri: environment.b2cPolicies.authorityMetadata.jwks_uri,
      }),
      postLogoutRedirectUri: '/',
      knownAuthorities: [environment.b2cPolicies.authorityDomain],
      navigateToLoginRequestUrl: true,
      protocolMode: ProtocolMode.OIDC,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
    },
    system: {
      // allowNativeBroker: false, // Disables WAM Broker
    },
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();

  protectedResourceMap.set(environment.apiConfig.uri, environment.apiConfig.scopes);
  protectedResourceMap.set(environment.azureUserProfileServiceConfig.uri, environment.azureUserProfileServiceConfig.scopes);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [...environment.apiConfig.scopes],
    },
    loginFailedRoute: '/login-failed',
  };
}

// It's important to call this before Angular bootstraps, so that the locales are registered
registerAppLocales();

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent, MsalRedirectComponent],
  imports: [
    BrowserModule,
    MsalModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    StoreModule.forRoot(ROOT_REDUCERS),
    EffectsModule.forRoot([ApplicationEffects]),
    StoreDevtoolsModule.instrument({ maxAge: 25, connectInZone: true }),
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: true,
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    }),
    TranslocoRootModule,
    SharedModule,
  ],
  providers: [
    HttpService,
    provideAppInitializer(initSnackbar),
    CxMaterialConfigProviders,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: OfflineInterceptor,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeMsal,
      deps: [MSAL_INSTANCE],
      multi: true,
    },
    // provideAppInitializer(() => {
    //     const initializerFn = (initializeMsal)(inject(MSAL_INSTANCE));
    //     return initializerFn();
    //   }),
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    {
      provide: LOCALE_ID,
      useFactory: getAppLocale,
    },
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: DEFAULT_DIALOG_OPTIONS,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {}

/**
 * APP_INITIALIZER to call msalInstance.initialize()
 */
export function initializeMsal(instance: IPublicClientApplication) {
  return () => instance.initialize();
}
